import { captureException } from '@sentry/react';
import { hoursToMilliseconds, startOfToday, subDays } from 'date-fns';
import { secondsToMilliseconds } from 'motion/react';
import { useCallback, useMemo } from 'react';
import { useEffectOnce, useInterval } from 'react-use';

import { SeenOrdersRepository } from '../sqlite';

export interface UseSeenOrdersDbValue {
  addOrders(orderIds: string[]): Promise<void>;
  getSeenOrderIds(): Promise<string[]>;
}

export const useSeenOrdersDb = (): UseSeenOrdersDbValue => {
  const repository = useMemo(() => new SeenOrdersRepository(), []);

  const addOrders = useCallback(
    async (orderIds: string[]) => {
      await repository.addOrders(orderIds);
    },
    [repository],
  );

  const getSeenOrderIds = useCallback(async () => {
    return await repository.getSeenOrderIds();
  }, [repository]);

  const cleanupOldOrders = useCallback(() => {
    const cutOffDate = subDays(startOfToday(), 3);

    console.log(
      `Cleaning up seen orders older than ${cutOffDate.toISOString()}`,
    );

    repository.cleanUpOldOrders(cutOffDate.getTime()).catch((error) => {
      console.error('Failed to clean up old seen orders:', error);

      captureException(error, {
        level: 'error',
        extra: { message: 'Failed to clean up old seen orders' },
      });
    });
  }, [repository]);

  useEffectOnce(() => {
    setTimeout(() => {
      cleanupOldOrders();
    }, secondsToMilliseconds(30));
  });

  useInterval(() => {
    cleanupOldOrders();
  }, hoursToMilliseconds(8));

  return {
    addOrders,
    getSeenOrderIds,
  };
};
